import './PanelSet.scss';
import { ButtonsMixin } from '@components/Mixin/ButtonsMixin/ButtonsMixin.js';
import { Row } from '@components/Component/Container/Row/Row.js';
import { forEachKey, isMobile } from '@components/Utility/index.js';
import { Panel } from '@components/Component/Container/Panel/Panel.js';
import { switchMap, of, take } from 'rxjs';
export class PanelSet extends ButtonsMixin(Row) {
    get currentPanelRight() { return this._currentPanelRight; }
    set currentPanelRight(panel) {
        this._propertySet('currentPanelRight', panel);
    }
    get currentPanelLeft() { return this._currentPanelLeft; }
    set currentPanelLeft(panel) {
        this._propertySet('currentPanelLeft', panel);
    }
    get showRightButtons() { return this._showRightButtons; }
    set showRightButtons(showRightButtons) {
        this._propertySet('showRightButtons', showRightButtons);
        if (this.drawn) {
            this.renderShowRightButtons();
        }
    }
    renderShowRightButtons() {
        const showRightButtons = this._showRightButtons;
        this.buttonContainers.get('').classList.toggle('d-none', !showRightButtons);
        this.contentContainer.classList.toggle('has-right-button-container', showRightButtons);
        this.panelContainerRight.classList.toggle('has-right-button-container', showRightButtons);
    }
    get showLeftButtons() { return this._showLeftButtons; }
    set showLeftButtons(showLeftButtons) {
        this._propertySet('showLeftButtons', showLeftButtons);
        if (this.drawn) {
            this.renderShowLeftButtons();
        }
    }
    renderShowLeftButtons() {
        const showLeftButtons = this._showLeftButtons;
        this.buttonContainers.get('left').classList.toggle('d-none', !showLeftButtons);
        this.contentContainer.classList.toggle('has-left-button-container', showLeftButtons);
        this.panelContainerLeft.classList.toggle('has-left-button-container', showLeftButtons);
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'PanelSet';
        this._showRightButtons = false;
        this._showLeftButtons = false;
        this.gutterX_xs = 'none';
    }
    draw() {
        super.draw();
        // TODO: panelset probably should not extend row.
        this.rootElement.classList.remove('row');
        this.panelContainerLeft = this.childContainerGet().querySelector(':scope > .panels-left');
        this.panelContainerRight = this.childContainerGet().querySelector(':scope > .panels-right');
        this.contentContainer = this.childContainerGet().querySelector(':scope > .content-container');
        this.renderShowRightButtons();
        this.renderShowLeftButtons();
        // set up borders properly for filter sets that have a grid as the first content child
        let firstContentItem = this.contentContainer?.firstElementChild;
        if (firstContentItem) {
            if (firstContentItem.classList.contains('s360Column')) {
                firstContentItem = firstContentItem.firstElementChild || firstContentItem;
            }
            if (firstContentItem.classList.contains('s360Grid')) {
                this.buttonContainerGet()?.classList.add('grid-filters');
                this.buttonContainerGet('left')?.classList.add('grid-filters');
            }
        }
        this.propertyGet$('currentPanelRight').pipe(switchMap((panel) => panel ? panel.propertyGet$('modalMode') : of(true))).subscribe((modalMode) => {
            this.rootElement.classList.toggle('panel-right-open', !modalMode);
        });
        this.propertyGet$('currentPanelLeft').pipe(switchMap((panel) => panel ? panel.propertyGet$('modalMode') : of(true))).subscribe((modalMode) => {
            this.rootElement.classList.toggle('panel-left-open', !modalMode);
        });
        this.isVisible$().pipe(take(1)).subscribe(() => {
            if (this.defaultRightPanelId && !this.currentPanelRight) {
                this.togglePanel(this.defaultRightPanelId);
            }
            if (this.defaultLeftPanelId && !this.currentPanelLeft) {
                this.togglePanel(this.defaultLeftPanelId);
            }
        });
    }
    componentAdd(component, appendToDOM = false) {
        if (component instanceof Panel) {
            this.buttonAdd({
                containerId: component.side === 'left' ? 'left' : '',
                icon: component.propertyGet$('icon'),
                id: `${component.id}_button`,
                index: Object.keys(this.components).length,
                captionKey: component.captionKey,
                onClick: () => {
                    this.togglePanel(component.id, false, true);
                }
            });
        }
        super.componentAdd(component, appendToDOM);
    }
    currentPanelGet(side) {
        return side === 'left' ? this.currentPanelLeft : this.currentPanelRight;
    }
    currentPanelSet(panel, side, slide = false) {
        const currentPanel = this.currentPanelGet(side);
        if (currentPanel === panel) {
            return;
        }
        if (isMobile() && panel) {
            const oppositeSide = side === 'left' ? 'right' : 'left';
            const oppositePanel = this.currentPanelGet(oppositeSide);
            if (oppositePanel) {
                this.currentPanelSet(undefined, oppositeSide, false);
            }
        }
        if (side === 'left') {
            this.currentPanelLeft = panel;
        }
        else {
            this.currentPanelRight = panel;
        }
        if (panel) {
            if (currentPanel) {
                currentPanel.hide();
            }
            const shouldSlide = slide && !currentPanel;
            this.togglePanelElements(true, side);
            this.buttonActiveSet(`${panel.id}_button`, true, true);
            panel.show();
            if (shouldSlide) {
                this.slidePanel(side, true, panel);
            }
            else if (isMobile()) {
                this.childContainerGet().classList.add('mobile-panel-open');
            }
        }
        else if (currentPanel) {
            this.buttonActiveSet(`${currentPanel.id}_button`, false, false);
            if (slide) {
                this.slidePanel(side, false, currentPanel, () => {
                    currentPanel.hide();
                    this.togglePanelElements(false, side);
                });
            }
            else {
                if (isMobile()) {
                    this.childContainerGet().classList.remove('mobile-panel-open');
                }
                currentPanel.hide();
                this.togglePanelElements(false, side);
            }
        }
    }
    slidePanel(side, open, panel, onComplete) {
        const panelContainer = side === 'left' ? this.panelContainerLeft : this.panelContainerRight;
        const duration = 250;
        const marginProp = side === 'left' ? 'marginLeft' : 'marginRight';
        const cssProp = side === 'left' ? 'margin-left' : 'margin-right';
        const childContainer = this.childContainerGet();
        if (isMobile()) {
            const targetWidth = childContainer.offsetWidth;
            panelContainer.style.flexShrink = '0';
            panelContainer.style.width = `${targetWidth}px`;
            if (open) {
                panelContainer.style[marginProp] = `-${targetWidth}px`;
                void panelContainer.offsetHeight;
                panelContainer.style.transition = `${cssProp} ${duration}ms ease`;
                panelContainer.style[marginProp] = '0';
            }
            else {
                childContainer.classList.remove('mobile-panel-open');
                void childContainer.offsetHeight;
                panelContainer.style.transition = `${cssProp} ${duration}ms ease`;
                panelContainer.style[marginProp] = `-${targetWidth}px`;
            }
        }
        else {
            const containerWidth = panelContainer.offsetWidth;
            if (open) {
                panelContainer.style[marginProp] = `-${containerWidth}px`;
                void panelContainer.offsetHeight;
                panelContainer.style.transition = `${cssProp} ${duration}ms ease`;
                panelContainer.style[marginProp] = '0';
            }
            else {
                panelContainer.style.transition = `${cssProp} ${duration}ms ease`;
                panelContainer.style[marginProp] = `-${containerWidth}px`;
            }
        }
        let cleaned = false;
        const cleanup = () => {
            if (cleaned)
                return;
            cleaned = true;
            clearTimeout(fallback);
            if (eventId)
                this.unRegisterEvent(eventId);
            panelContainer.style.transition = '';
            panelContainer.style[marginProp] = '';
            panelContainer.style.flexShrink = '';
            panelContainer.style.width = '';
            if (isMobile() && open) {
                childContainer.classList.add('mobile-panel-open');
            }
            onComplete?.();
        };
        const eventId = this.registerEvent('transitionend', panelContainer, (e) => {
            if (e.target !== panelContainer)
                return;
            cleanup();
        });
        const fallback = setTimeout(cleanup, duration + 100);
    }
    togglePanelElements(show, side) {
        const left = side === 'left';
        const panelContainer = left ? this.panelContainerLeft : this.panelContainerRight;
        const buttonContainer = this.buttonContainerGet(side === 'left' ? 'left' : '');
        if (buttonContainer) {
            buttonContainer.classList.toggle('open', show);
        }
        if (panelContainer) {
            panelContainer.classList.toggle('d-none', !show);
        }
        const contentContainer = this.rootElement.querySelector(':scope > .child-container > .content-container');
        if (contentContainer) {
            contentContainer.classList.toggle('panel-open', show);
            contentContainer.classList.toggle(`panel-${side}-open`, show);
        }
    }
    buttonContainerCreate() {
        const buttonContainer = this.rootElement.querySelector('.button-container-right');
        this.buttonContainers.set('', buttonContainer);
        const buttonContainerLeft = this.rootElement.querySelector('.button-container-left');
        this.buttonContainers.set('left', buttonContainerLeft);
    }
    togglePanel(id, forceOpen = false, slide = false) {
        const panel = this.components[id];
        if (!panel) {
            return;
        }
        const side = panel.side;
        const currentPanel = this.currentPanelGet(side);
        if (!forceOpen && panel === currentPanel) {
            this.currentPanelSet(undefined, side, slide);
        }
        else {
            this.currentPanelSet(panel, side, slide);
        }
    }
    appendChildComponent(childComponent) {
        console.log('rendering children from panel');
        if (!childComponent.rootElement) {
            childComponent.rootElement = document.createElement(childComponent.rootElementType);
            childComponent.rootElement.setAttribute('data-componentid', childComponent.id);
        }
        if (childComponent instanceof Panel) {
            if (childComponent.side === 'left') {
                this.panelContainerLeft?.appendChild(childComponent.rootElement);
            }
            else {
                this.panelContainerRight?.appendChild(childComponent.rootElement);
            }
        }
        else {
            this.contentContainer?.appendChild(childComponent.rootElement);
        }
    }
    generateChildTemplate() {
        const panelsLeft = [];
        const panelsRight = [];
        const contentComponents = [];
        forEachKey(this.components, (component, id) => {
            if (component instanceof Panel) {
                if (component.side === 'left') {
                    panelsLeft.push(component);
                }
                else {
                    panelsRight.push(component);
                }
            }
            else {
                contentComponents.push(component);
            }
        });
        const leftPanelTemplate = this.panelContainerTemplate('left', panelsLeft);
        const rightPanelTemplate = this.panelContainerTemplate('right', panelsRight);
        const contentTemplate = this.contentContainerTemplate(contentComponents);
        return leftPanelTemplate + contentTemplate + rightPanelTemplate;
    }
    panelContainerTemplate(side, components) {
        let template = `<div class="panels panels-${side} d-none" >`;
        components.forEach((component) => {
            template += `<${component.rootElementType} data-componentid="${component.id}"></${component.rootElementType}>`;
        });
        template += '</div>';
        return template;
    }
    contentContainerTemplate(components) {
        let template = '<div class="content-container row">';
        components.forEach((component) => {
            template += `<${component.rootElementType} data-componentid="${component.id}"></${component.rootElementType}>`;
        });
        template += '</div>';
        return template;
    }
    template() {
        const style = !this.showLeftButtons ? 'd-none' : '';
        const styleRight = !this.showRightButtons ? 'd-none' : '';
        return `<div class="button-container button-container-left ${style}" ></div><div class="child-container"></div><div class="button-container button-container-right ${styleRight}"></div>`;
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        delete properties.gutterX_xs;
        delete properties.gutterY_xs;
        properties.defaultRightPanelId = ({
            id: 'defaultRightPanelId',
            value: this.defaultRightPanelId,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'None - Collapsed' },
                    ...Object.values(this.components)
                        .filter((c) => {
                        return c instanceof Panel && c.side !== 'left';
                    })
                        .map(c => {
                        return {
                            value: c.id
                        };
                    })
                ]
            },
            captionKey: 'formbuilder_panelset_defaultrightpanelid',
            propertyGroup: '',
            saveFlag: () => !!this.defaultRightPanelId
        });
        properties.defaultLeftPanelId = ({
            id: 'defaultLeftPanelId',
            value: this.defaultLeftPanelId,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'None - Collapsed' },
                    ...Object.values(this.components)
                        .filter((c) => {
                        return c instanceof Panel && c.side === 'left';
                    })
                        .map(c => {
                        return {
                            value: c.id
                        };
                    })
                ]
            },
            captionKey: 'formbuilder_panelset_defaultleftpanelid',
            propertyGroup: '',
            saveFlag: () => !!this.defaultLeftPanelId
        });
        return properties;
    }
}
PanelSet.VERSION = '$Revision: 113901 $';
